import React, { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useLaborRules,
  useUpsertLaborRule,
  type LaborRule,
  type LaborRuleUpsertInput,
} from '@/hooks/optimization/useOptimizationSettings';
import { usePositions } from '@/hooks/usePositions';

const DEFAULTS: Required<LaborRuleUpsertInput> = {
  max_hours_per_week: 40,
  min_rest_hours_between_shifts: 12,
  min_days_off_per_week: 1,
  max_consecutive_work_days: 6,
  max_hours_per_day: 12,
  allow_night_shifts: false,
  is_active: true,
};

export const LaborRulesTab: React.FC = () => {
  const { data: positions } = usePositions({ limit: 200 });
  const { data: allRules } = useLaborRules();
  const upsert = useUpsertLaborRule();

  const [selectedPositionId, setSelectedPositionId] = useState<string>('');
  const [form, setForm] = useState<Required<LaborRuleUpsertInput>>(DEFAULTS);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const ruleByPosition = useMemo(() => {
    const m = new Map<string, LaborRule>();
    for (const r of allRules ?? []) {
      m.set(r.position_id, r);
    }
    return m;
  }, [allRules]);

  const selectedRule = selectedPositionId
    ? ruleByPosition.get(selectedPositionId)
    : undefined;

  // Подгружаем данные правила при выборе позиции
  React.useEffect(() => {
    if (selectedRule) {
      setForm({
        max_hours_per_week: selectedRule.max_hours_per_week,
        min_rest_hours_between_shifts:
          selectedRule.min_rest_hours_between_shifts,
        min_days_off_per_week: selectedRule.min_days_off_per_week,
        max_consecutive_work_days: selectedRule.max_consecutive_work_days,
        max_hours_per_day: selectedRule.max_hours_per_day,
        allow_night_shifts: selectedRule.allow_night_shifts,
        is_active: selectedRule.is_active,
      });
    } else if (selectedPositionId) {
      setForm(DEFAULTS);
    }
    setSuccess(false);
    setError('');
  }, [selectedPositionId, selectedRule]);

  const submit = async () => {
    if (!selectedPositionId) return;
    setError('');
    setSuccess(false);
    try {
      await upsert.mutateAsync({
        positionId: selectedPositionId,
        data: form,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка');
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Правила трудового распорядка задаются по должности и применяются ко всем
        сотрудникам этой должности.
      </p>

      <div>
        <Label>Должность</Label>
        <Select
          value={selectedPositionId}
          onValueChange={setSelectedPositionId}
        >
          <SelectTrigger>
            <SelectValue placeholder="Выберите должность" />
          </SelectTrigger>
          <SelectContent>
            {(positions?.data ?? []).map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
                {ruleByPosition.has(p.id) ? ' • настроено' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedPositionId && (
        <div className="space-y-4 border rounded-md p-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Макс. часов в неделю"
              value={form.max_hours_per_week}
              min={1}
              max={168}
              onChange={(v) => setForm({ ...form, max_hours_per_week: v })}
            />
            <NumberField
              label="Мин. часов отдыха между сменами"
              value={form.min_rest_hours_between_shifts}
              min={0}
              max={48}
              onChange={(v) =>
                setForm({ ...form, min_rest_hours_between_shifts: v })
              }
            />
            <NumberField
              label="Мин. выходных в неделю"
              value={form.min_days_off_per_week}
              min={0}
              max={7}
              onChange={(v) => setForm({ ...form, min_days_off_per_week: v })}
            />
            <NumberField
              label="Макс. рабочих дней подряд"
              value={form.max_consecutive_work_days}
              min={1}
              max={31}
              onChange={(v) =>
                setForm({ ...form, max_consecutive_work_days: v })
              }
            />
            <NumberField
              label="Макс. часов в день"
              value={form.max_hours_per_day}
              min={1}
              max={24}
              onChange={(v) => setForm({ ...form, max_hours_per_day: v })}
            />
            <label className="flex items-center gap-2 text-sm pt-7">
              <input
                type="checkbox"
                checked={form.allow_night_shifts}
                onChange={(e) =>
                  setForm({ ...form, allow_night_shifts: e.target.checked })
                }
              />
              Разрешены ночные смены
            </label>
          </div>

          {success && (
            <Alert className="border-green-300 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Правила сохранены
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end">
            <Button onClick={submit} disabled={upsert.isPending}>
              {upsert.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {selectedRule ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const NumberField: React.FC<{
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, onChange }) => (
  <div>
    <Label>{label}</Label>
    <Input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
    />
  </div>
);
